import {
  Dropdown,
  Grid,
  GridColumn,
  Link,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableHeaderCell,
  TableHeaderRow,
  TableRow
} from "@modernatx/ui-kit-react";
import React from "react";

import { CreditListProps, CreditSortableColumnKeys } from "@/types/Credit";
import { PageSize, SortOrder } from "@/types/Invoice";
import {
  formatAddress,
  formatCreditStatus,
  formatCurrency,
  formatTimestampDate
} from "@/utils/InvoicePay";

const CreditList: React.FC<CreditListProps> = ({
  creditList,
  pageSize,
  totalPage,
  totalCredits,
  currentPage,
  onPageChange,
  onPageSizeChange,
  onSort,
  sortProperty,
  sortDirection
}) => {
  const handleSortClick = (property: CreditSortableColumnKeys) => {
    const newSortDirection =
      sortProperty === property && sortDirection === SortOrder.ASC
        ? SortOrder.DESC
        : SortOrder.ASC;
    onSort({
      property,
      direction: newSortDirection
    });
  };

  return (
    <Grid>
      <GridColumn>
        <Table>
          <TableHeaderRow>
            <TableRow>
              <TableHeaderCell
                key="creditNo"
                onClick={() =>
                  handleSortClick(CreditSortableColumnKeys.CreditNo)
                }
                sortable={true}
                sortKey={CreditSortableColumnKeys.CreditNo}
                sortDirection={
                  sortProperty === CreditSortableColumnKeys.CreditNo
                    ? sortDirection
                    : undefined
                }
              >
                Credit number
              </TableHeaderCell>
              <TableHeaderCell
                key="poNumber"
                onClick={() =>
                  handleSortClick(CreditSortableColumnKeys.PONumber)
                }
                sortable={true}
                sortKey={CreditSortableColumnKeys.PONumber}
                sortDirection={
                  sortProperty === CreditSortableColumnKeys.PONumber
                    ? sortDirection
                    : undefined
                }
              >
                P.O. number
              </TableHeaderCell>
              <TableHeaderCell
                key="shipTo"
                onClick={() => handleSortClick(CreditSortableColumnKeys.ShipTo)}
                sortable={true}
                sortKey={CreditSortableColumnKeys.ShipTo}
                sortDirection={
                  sortProperty === CreditSortableColumnKeys.ShipTo
                    ? sortDirection
                    : undefined
                }
                grow={1.5}
              >
                Shipping location
              </TableHeaderCell>
              <TableHeaderCell
                key="creditDate"
                onClick={() =>
                  handleSortClick(CreditSortableColumnKeys.CreditDate)
                }
                sortable={true}
                sortKey={CreditSortableColumnKeys.CreditDate}
                sortDirection={
                  sortProperty === CreditSortableColumnKeys.CreditDate
                    ? sortDirection
                    : undefined
                }
              >
                Date issued
              </TableHeaderCell>
              <TableHeaderCell
                key="total"
                onClick={() => handleSortClick(CreditSortableColumnKeys.Total)}
                sortable={true}
                sortKey={CreditSortableColumnKeys.Total}
                sortDirection={
                  sortProperty === CreditSortableColumnKeys.Total
                    ? sortDirection
                    : undefined
                }
              >
                Credit amount
              </TableHeaderCell>
              <TableHeaderCell
                key="status"
                onClick={() => handleSortClick(CreditSortableColumnKeys.Status)}
                sortable={true}
                sortKey={CreditSortableColumnKeys.Status}
                sortDirection={
                  sortProperty === CreditSortableColumnKeys.Status
                    ? sortDirection
                    : undefined
                }
              >
                Status
              </TableHeaderCell>
            </TableRow>
          </TableHeaderRow>
          <TableBody>
            {creditList.map((credit, index) => (
              <TableRow key={index}>
                <TableCell key="creditNo">
                  <Link
                    href={`/your-account/invoices-and-payments/credits/${credit.creditNo}`}
                  >
                    {credit.creditNo}
                  </Link>
                </TableCell>
                <TableCell key="poNumber">{credit.poNumber || "--"}</TableCell>
                <TableCell key="shipTo">
                  {formatAddress(credit.shipTo)}
                </TableCell>
                <TableCell key="creditDate">
                  {formatTimestampDate(credit.creditDate)}
                </TableCell>
                <TableCell key="total">
                  {formatCurrency(credit.total, credit.currency)}
                </TableCell>
                <TableCell key="status">
                  {formatCreditStatus(credit.status)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Grid sx={{ mt: "spacing-9" }}>
          <GridColumn push={[0, 3, 5]} size={2}>
            {totalPage > 1 && (
              <Pagination
                page={currentPage}
                count={totalPage}
                onPageChange={onPageChange}
              />
            )}
          </GridColumn>
          <GridColumn push={[0, 1, 3]} size={2}>
            {totalCredits > 10 && (
              <Dropdown
                label="No. per page"
                defaultOption={{ label: String(pageSize), value: pageSize }}
                dropdownOptions={[
                  { label: "10", value: 10 },
                  { label: "50", value: 50 },
                  { label: "100", value: 100 }
                ]}
                onSelectOption={({ value }) =>
                  onPageSizeChange(Number(value) as PageSize)
                }
              />
            )}
          </GridColumn>
        </Grid>
      </GridColumn>
    </Grid>
  );
};

export default CreditList;
